/**
 * OptionalRecipeToggles Component
 * Toggle switches for optional recipes in RSVP flow.
 * Default: all included (ON). Toggling OFF opts out of that recipe.
 */

import { cn } from '@/lib/utils';
import { formatOptOutSummary } from '@/lib/utils/optOutHelpers';
import type { MenuRecipeLink } from '@/types/api';

interface OptionalRecipeTogglesProps {
  recipes: MenuRecipeLink[];
  optedOutIds: Set<number>;
  onChange: (menuRecipeId: number, included: boolean) => void;
  disabled?: boolean;
  readOnly?: boolean;
}

/**
 * Builds a read-only summary string from opt-out state.
 * Returns "All included" or "No Naan, No Rice" etc.
 */
function getOptOutSummary(
  optionalRecipes: MenuRecipeLink[],
  optedOutIds: Set<number>
): string {
  return formatOptOutSummary(
    optionalRecipes.map((r) => ({
      recipeName: r.name,
      optedOut: optedOutIds.has(r.menuRecipeId),
    }))
  );
}

export function OptionalRecipeToggles({
  recipes,
  optedOutIds,
  onChange,
  disabled = false,
  readOnly = false,
}: OptionalRecipeTogglesProps) {
  const optionalRecipes = recipes
    .filter((r) => r.isOptional)
    .sort((a, b) => a.menuRecipeId - b.menuRecipeId);

  if (optionalRecipes.length === 0) return null;

  if (readOnly) {
    const summary = getOptOutSummary(optionalRecipes, optedOutIds);
    const hasOptOuts = optionalRecipes.some((r) => optedOutIds.has(r.menuRecipeId));
    return (
      <p className={cn(
        'text-xs',
        hasOptOuts ? 'text-amber-600' : 'text-muted-foreground'
      )}>
        {summary}
      </p>
    );
  }

  return (
    <div className="mt-2 space-y-1.5">
      {optionalRecipes.map((recipe) => {
        const included = !optedOutIds.has(recipe.menuRecipeId);
        return (
          <label
            key={recipe.menuRecipeId}
            className={cn(
              'flex items-center gap-2 cursor-pointer select-none',
              disabled && 'cursor-not-allowed opacity-60'
            )}
          >
            <button
              type="button"
              role="switch"
              aria-checked={included}
              aria-label={`${recipe.name}: ${included ? 'included' : 'opted out'}`}
              disabled={disabled}
              onClick={() => onChange(recipe.menuRecipeId, !included)}
              className={cn(
                'relative inline-flex h-5 w-9 shrink-0 rounded-full border-2 border-transparent',
                'transition-colors duration-200 ease-in-out',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
                'disabled:cursor-not-allowed disabled:opacity-50',
                included ? 'bg-primary' : 'bg-muted-foreground/30'
              )}
            >
              <span
                className={cn(
                  'pointer-events-none inline-block h-4 w-4 rounded-full bg-background shadow-sm',
                  'transform transition-transform duration-200 ease-in-out',
                  included ? 'translate-x-4' : 'translate-x-0'
                )}
              />
            </button>
            <span className={cn(
              'text-xs',
              included ? 'text-foreground' : 'text-muted-foreground'
            )}>
              {recipe.name}
            </span>
          </label>
        );
      })}
    </div>
  );
}
